import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Slide from '@material-ui/core/Slide';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { TransitionProps } from '@material-ui/core/transitions';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CloseIcon from '@material-ui/icons/Close';
import PropTypes from 'prop-types';
import type { FC, ReactChild, ReactChildren, ReactNode } from 'react';
import React, { ReactFragment } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Theme } from 'src/theme';

export interface DisableButtonsInterface {
  ok?: boolean;
  close?: boolean;
}
interface ResponsiveDialogProps {
  title?: string | ReactFragment;
  handleClose?: () => void;
  handleOK?: () => void;
  open?: boolean;
  showTitle?: boolean;
  okBtnText?: string;
  closeBtnText?: string;
  children: ReactChild | ReactChildren;
  actions?: ReactNode;
  onlyOkBtn?: boolean;
  onlyCloseBtn?: boolean;
  style?: object;
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false;
  fullWidth?: boolean;
  disableBackdropClickClose?: boolean;
  showCloseButton?: boolean;
  disableButtons?: DisableButtonsInterface;
  keepMounted?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: { minWidth: '300px' },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ResponsiveDialog: FC<ResponsiveDialogProps> = (
  {
    title,
    handleClose = () => { },
    handleOK = () => { },
    open = true,
    children,
    actions = null,
    okBtnText = 'OK',
    closeBtnText = 'Close',
    onlyOkBtn = false,
    onlyCloseBtn = false,
    style = {},
    maxWidth = false,
    fullWidth = false,
    disableBackdropClickClose = false,
    showTitle = true,
    showCloseButton = false,
    disableButtons = { 'ok': false, 'close': false },
    keepMounted = true,

  }) => {
  const theme = useTheme();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <div>
      <Dialog
        keepMounted={keepMounted}
        TransitionComponent={Transition}
        fullScreen={fullScreen}
        open={open}
        onClose={(event, reason) => {
          if (!disableBackdropClickClose) {
            handleClose();
          } else if (disableBackdropClickClose && reason !== 'backdropClick') {
            handleClose();
          }
        }}
        aria-labelledby="responsive-dialog-title"
        classes={{ paper: classes.paper }}
        style={style}
        fullWidth={fullWidth}
        maxWidth={maxWidth as 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false}
      >
        {showTitle ? <DialogTitle id="responsive-dialog-title" >{title}
        </DialogTitle> : ''}
        {showCloseButton ? <DialogTitle id="responsive-dialog-title"><IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton></DialogTitle> : ''}

        <PerfectScrollbar>
          <DialogContent>
            <DialogContentText>
              {children}
            </DialogContentText>
          </DialogContent>
        </PerfectScrollbar>
        {actions ? actions : <DialogActions>
          {!onlyOkBtn ? <Button disabled={disableButtons.close} onClick={handleClose} color="primary">
            {closeBtnText}
          </Button> : ''}
          {!onlyCloseBtn ? <Button disabled={disableButtons.ok} onClick={handleOK} color="primary">
            {okBtnText}
          </Button> : ''}
        </DialogActions>}
      </Dialog>
    </div>
  );
};

ResponsiveDialog.propTypes = {
  handleClose: PropTypes.func,
  title: PropTypes.string
};

export default ResponsiveDialog;